import React from 'react';
import { CheckCircle2, Globe as Globe2, Users, ChevronDown, MapPin, Compass } from 'lucide-react';
import { getCountriesFromFirebase } from '../../lib/firebase-service';
import { useIsPWA } from '../../hooks/useIsPWA';

interface HeroSectionProps {
  onLogin: () => void;
  countriesCount: number;
}

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1539635278303-d4002c07eae3?q=80&w=2670&auto=format&fit=crop';

export function HeroSection({ onLogin, countriesCount }: HeroSectionProps) {
  const [heroImage, setHeroImage] = React.useState(FALLBACK_IMAGE);
  const [isImageLoading, setIsImageLoading] = React.useState(true);
  const [scrollY, setScrollY] = React.useState(0);
  const isPWA = useIsPWA();

  React.useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });

    const fetchRandomCountry = async () => {
      try {
        const countries = await getCountriesFromFirebase();
        if (countries && countries.length > 0) {
          const randomCountry = countries[Math.floor(Math.random() * countries.length)];
          const img = new Image();
          img.onload = () => {
            setHeroImage(randomCountry.image);
            setIsImageLoading(false);
          };
          img.onerror = () => {
            setHeroImage(FALLBACK_IMAGE);
            setIsImageLoading(false);
          };
          img.src = randomCountry.image;
        } else {
          setIsImageLoading(false);
        }
      } catch {
        setIsImageLoading(false);
      }
    };

    fetchRandomCountry();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight * 0.85, behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="Hero background"
          className={`w-full h-full object-cover transition-opacity duration-700 ${
            isImageLoading ? 'opacity-0' : 'opacity-100'
          }`}
          style={{ transform: `scale(1.1) translateY(${scrollY * 0.15}px)` }}
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent" />
      </div>

      <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-black/40 to-transparent z-[1]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-0 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8 animate-fade-in">
              <Compass className="w-4 h-4 text-accent-400" />
              <span className="text-sm text-white/90 font-medium">La communaut&eacute; n&deg;1 des Pvtistes</span>
            </div>

            <h1 className="font-bold mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <span className="block text-4xl sm:text-5xl md:text-6xl xl:text-7xl leading-[1.1] text-white">
                Organisez votre
              </span>
              <span className="block text-4xl sm:text-5xl md:text-6xl xl:text-7xl leading-[1.1] mt-2">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-400 to-accent-300">
                  PVT
                </span>
                <span className="text-white"> simplement</span>
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-white/75 mb-10 leading-relaxed max-w-xl mx-auto lg:mx-0 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Checklist, budget, photos, PVTmate&hellip; tout est r&eacute;uni dans une seule app pour t&rsquo;accompagner &agrave; chaque &eacute;tape de l&rsquo;aventure.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <button
                onClick={onLogin}
                className="group px-8 py-4 bg-accent-500 hover:bg-accent-600 text-white rounded-2xl font-semibold text-base shadow-xl shadow-accent-500/25 hover:shadow-2xl hover:shadow-accent-500/40 transition-all duration-300 hover:-translate-y-0.5"
              >
                Commencer gratuitement
              </button>
              {!isPWA && (
                <div className="flex flex-row gap-3 justify-center items-center">
                  <a
                    href="https://apps.apple.com/ch/app/pvtistes-pvt-whv/id1151407622?l=fr-FR"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="https://pvtistes.net/wp-content/themes/pvtistest-2/static/ui/apple_store_badge.png"
                      alt="Download on the App Store"
                      className="h-11 sm:h-12 w-auto opacity-80 hover:opacity-100 transition-opacity"
                    />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.degordian.pvtistes"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="https://pvtistes.net/wp-content/themes/pvtistest-2/static/ui/google-play-badge.png"
                      alt="Disponible sur Google Play"
                      className="h-11 sm:h-12 w-auto opacity-80 hover:opacity-100 transition-opacity"
                    />
                  </a>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-6 sm:gap-10 justify-center lg:justify-start animate-fade-in" style={{ animationDelay: '0.4s' }}>
              {[
                { icon: CheckCircle2, label: 'Gratuit' },
                { icon: Users, label: '500k+ membres' },
                { icon: Globe2, label: `${countriesCount}+ pays` },
              ].map((stat) => (
                <div key={stat.label} className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                    <stat.icon className="w-4 h-4 text-accent-400" />
                  </div>
                  <span className="text-sm sm:text-base text-white/80 font-medium">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:flex flex-col items-center justify-center gap-6">
            <div className="relative">
              <div className="absolute -inset-8 bg-accent-500/10 rounded-3xl blur-3xl" />
              <div className="relative grid grid-cols-2 gap-4">
                {[
                  { icon: MapPin, title: 'Checklist', desc: 'Par destination' },
                  { icon: Globe2, title: 'Budget', desc: 'Donn\u00e9es r\u00e9elles' },
                  { icon: Users, title: 'PVTmates', desc: 'Trouve ton duo' },
                  { icon: Compass, title: 'Guides', desc: 'Conseils experts' },
                ].map((card) => (
                  <div
                    key={card.title}
                    className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 hover:bg-white/15 transition-all duration-300 group cursor-default"
                  >
                    <card.icon className="w-6 h-6 text-accent-400 mb-3 group-hover:scale-110 transition-transform" />
                    <div className="text-sm font-semibold text-white mb-1">{card.title}</div>
                    <div className="text-xs text-white/60">{card.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={scrollToContent}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 p-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all duration-300 animate-bounce-subtle"
      >
        <ChevronDown className="w-5 h-5" />
      </button>
    </section>
  );
}
